import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import HeaderToolbarContent from '../../../../components/TimeOnTaskReport/HeaderToolbar';
import usePrintPdfGenerator from '../../../../utils/PrintPdfGenerator';
import PageSize from '../../../../enums/pageSize';
import { useGetIncompleteSelfStudyMutation, useGetSelfStudyCumulativeSummaryMutation, useSelfStudyReportQuery } from '../../../../features/authentication/reportApiSlice';
import DetailedStatusGrid from '../../../../components/DetailedStatusGrid';
import SelfStudyTable from './components/selfStudyTable';
import IncompleteSessionsTable from './components/incompleteSessionsTable';
import Skeleton from 'react-loading-skeleton';
import { faTrophyStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SelfStudyClassReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCourseIds, selectedClassIds, selectedStudentIds, filters } = location.state;
  const [reportDetails, setReportDetails] = useState([]);
  const { data: selfStudyData, isLoading } = useSelfStudyReportQuery({ classIds: selectedClassIds, courseIds: selectedCourseIds, studentIds: selectedStudentIds });
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();
  const [showIncompleteSessionsReport, setShowIncompletedSessionsReport] = useState(false);
  const [incompleteReportDetails, setIncompleteReportDetails] = useState([]);
  const [ getCumulativeSummary, { data: cumulativeData, isLoading: isCumulativeSummaryLoading } ] = useGetSelfStudyCumulativeSummaryMutation();
  const [ getIncompleteSelfStudy, { data: incompletedSelfStudyData , isLoading: isIncompletedSelfStudyLoading }] = useGetIncompleteSelfStudyMutation();

  useEffect(() => {
    getCumulativeSummary({
      courseIds: selectedCourseIds,
      classIds: selectedClassIds,
      studentIds: selectedStudentIds
    });
  }, []);

  useEffect(() => {
    if (selfStudyData) {
      setReportDetails(selfStudyData);
    }
  }, [selfStudyData]);

  useEffect(() => {
    if(showIncompleteSessionsReport && !incompletedSelfStudyData)
    {
      getIncompleteSelfStudy({
        courseIds: selectedCourseIds,
        classIds: selectedClassIds,
        studentIds: selectedStudentIds
      });
    }
  }, [showIncompleteSessionsReport]);

  useEffect(() => {
    if (incompletedSelfStudyData) {
      setIncompleteReportDetails(incompletedSelfStudyData);
    }
  }, [incompletedSelfStudyData]);

  const handlePrintPdf = async () => {
    if(isPdfGenerating) return;

    const contentWrapper = document.getElementById('content');
    const contentClone = contentWrapper.cloneNode(true);

    const header = document.createElement('header');
    header.textContent = showIncompleteSessionsReport ? 'Incomplete Self Study Sessions' : 'Self Study Report';
    header.style.fontWeight = 'bold';
    header.style.fontSize = '16px';
    contentClone.insertBefore(header, contentClone.firstChild);

    const sortButtons = contentClone.querySelectorAll('.assignment-sort-btn');
    sortButtons.forEach((div) => {
      div.classList.add('d-none');
    });

    const buttons = contentClone.querySelectorAll('.btn');
    buttons.forEach((btn) => {
      btn.classList.add('d-none');
    });

    const btnTiles = contentClone.querySelectorAll('.action-btn-tile');
    btnTiles.forEach((btn) => {
      btn.classList.add('d-none');
    });
    
    await printPDF(showIncompleteSessionsReport ? 'IncompleteSelfStudySessionsReport' : 'SelfStudyReport', contentClone.innerHTML, PageSize.A4, true);
  };

  const combinedData = React.useMemo(() => {
    const totalStudents = cumulativeData?.totalStudents || 0;
    const totalSessions = cumulativeData?.totalSessions || 0;
    const completed = cumulativeData?.totalCompleted || 0;
    const completionRate = totalSessions > 0 ? Math.round((completed / totalSessions) * 100) : 0;
    const handleOnClick = () => {
      setShowIncompletedSessionsReport(true);
    };

    return [
      { label: 'Total Students', value: totalStudents },
      { label: 'Total Sessions', value: totalSessions },
      { label: 'Completed', value: completed },
      { label: 'Completion Rate', value: `${completionRate}%`, icon: <FontAwesomeIcon icon={faTrophyStar} size={20} className='m-0'/>, onClick: handleOnClick }
    ];
  }, [cumulativeData]);

  const handleBackButtonClick = () => {
    if(showIncompleteSessionsReport)
      setShowIncompletedSessionsReport(false);
    else
      window.history.back();
  };

  const handleNavigate = ()=>{
    navigate('self_study_cumulativereport', { state: { filters : filters } });
  };

  return (
    <div className='white-card'>
      <HeaderToolbarContent
        reportTitle={showIncompleteSessionsReport ? 'Incomplete Self Study Sessions' : 'Self Study Report'}
        printHandler={handlePrintPdf}
        isExporting={false}
        isDownloading={isPdfGenerating}
        hideClassDropdown={true}
        backButtonOnClick={() => handleBackButtonClick()}
      />
      <div id='content'>
        <div className='student_response_details'>
          {isCumulativeSummaryLoading ? <Skeleton className='h-35 w-min-max-250' /> : <h5 className='fs-16'>Cumulative Summary</h5>}
          <DetailedStatusGrid
            isLoading={isCumulativeSummaryLoading}
            data={combinedData}
            showLink={!showIncompleteSessionsReport}
          />
          {isCumulativeSummaryLoading ?
            <Skeleton className='h-35 w-min-max-250' />
            :
            <div className='w-100 text-start'>
              <button className={`btn ${reportDetails.length > 0 ? 'btn-castleOrange' : 'btn-castleGray disable-Pointer-Events text-black-50'}`} onClick={()=> handleNavigate() }>View Cumulative Report</button>
            </div>
          }
        </div>
        <div className='custom-table mt-4'>
          {showIncompleteSessionsReport ? (
            <IncompleteSessionsTable
              data={incompleteReportDetails}
              setData={setIncompleteReportDetails}
              isLoading={isIncompletedSelfStudyLoading}
            />
          ) : (
            <SelfStudyTable
              data={reportDetails}
              setData={setReportDetails}
              isLoading={isLoading}
              filters={filters}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfStudyClassReport;